import React, { useState, useEffect } from 'react';
import { Trash2, Eye, BarChart3 } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { API_ENDPOINTS } from '../../../config.js';
import './AnalysisHistory.css';

const AnalysisHistory = ({ userId, onViewAnalysis }) => {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    isOpen: false,
    analysisId: null,
    universityName: null
  });

  // Fetch analyses on mount
  useEffect(() => {
    const fetchAnalyses = async () => {
      try {
        setLoading(true);
        setError(null);

        const endpoint = API_ENDPOINTS.getUserAnalyses(userId);
        console.log('[AnalysisHistory] Fetching from:', endpoint);
        
        const response = await fetch(endpoint);
        
        console.log('[AnalysisHistory] Response status:', response.status, response.statusText);
        
        const contentType = response.headers.get('content-type') || '';
        console.log('[AnalysisHistory] Content-Type:', contentType);

        // Log first 500 chars of response body for debugging
        const responseText = await response.text();
        console.log('[AnalysisHistory] Response body (first 500 chars):', responseText.substring(0, 500));
        
        if (!response.ok) {
          throw new Error(`API Error: ${response.status} ${response.statusText}. Body: ${responseText.substring(0, 200)}`);
        }

        if (!contentType.includes('application/json')) {
          throw new Error(`Unexpected response format (got ${contentType || 'unknown'}). Body: ${responseText.substring(0, 200)}`);
        }

        let data;
        try {
          data = JSON.parse(responseText);
        } catch (parseErr) {
          throw new Error(`JSON parse failed: ${parseErr.message}. Body: ${responseText.substring(0, 200)}`);
        }

        if (!data.success) {
          throw new Error(data.error || 'Failed to fetch analyses');
        }

        setAnalyses(data.data || []);
      } catch (err) {
        console.error('Error fetching analyses:', err);
        setError(err.message || 'Could not load analyses. Please try again.');
        // Set empty array on error instead of showing error to user
        setAnalyses([]);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchAnalyses();
    }
  }, [userId]);

  const handleDeleteClick = (analysisId, universityName) => {
    setDeleteConfirmModal({
      isOpen: true,
      analysisId,
      universityName
    });
  };

  const handleConfirmDelete = async () => {
    const { analysisId } = deleteConfirmModal;
    try {
      const response = await fetch(API_ENDPOINTS.deleteAnalysis(analysisId), { method: 'DELETE' });
      const text = await response.text();
      const json = text ? JSON.parse(text) : null;

      if (!response.ok || (json && json.success === false)) {
        const msg = json?.error || `Delete failed (${response.status})`;
        console.error('[Delete analysis] backend error:', msg, 'body:', text);
        alert(`Delete failed: ${msg}`);
        return;
      }

      setAnalyses(analyses.filter(a => a.id !== analysisId));
      setDeleteConfirmModal({ isOpen: false, analysisId: null, universityName: null });
    } catch (err) {
      console.error('Error deleting analysis:', err);
      alert('Delete failed. Please check the console for details.');
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmModal({ isOpen: false, analysisId: null, universityName: null });
  };

  const getChanceColor = (percentage) => {
    if (percentage >= 70) return '#10b981'; // green
    if (percentage >= 50) return '#f59e0b'; // amber
    if (percentage >= 30) return '#ef4444'; // red
    return '#6b7280'; // gray
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'safety': '🎯 Safety School',
      'target': '🎲 Target School',
      'reach': '🚀 Reach School',
      'far-reach': '🌟 Far Reach School'
    };
    return labels[category] || category;
  };

  const filteredAnalyses = filter === 'all'
    ? analyses
    : analyses.filter(a => a.analysis_category === filter);

  if (loading) {
    return (
      <div className="history-container">
        <div className="history-loading">
          <div className="history-spinner"></div>
          <p>Loading your analyses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="history-container">
        <div className="history-error">
          <p>Error loading analyses: {error}</p>
        </div>
      </div>
    );
  }

  if (analyses.length === 0) {
    return (
      <div className="history-container">
        <div className="history-empty-card">
          <div className="history-empty-icon">
            <BarChart3 size={32} />
          </div>
          <div className="history-empty-body">
            <h3>No analyses yet</h3>
            <p>Run your first admission analysis to see tailored insights and history here.</p>
          </div>
          <div className="history-empty-actions">
            <a href="/universities/analyse" className="history-empty-btn">Start analyzing</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="history-container">
      <div className="history-header">
        <h2 className="history-title">Your Analysis History</h2>
        <p className="history-subtitle">{analyses.length} total analyses</p>
      </div>

      {/* Filter Tabs */}
      <div className="history-filters">
        <button
          className={`history-filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({analyses.length})
        </button>
        {['safety', 'target', 'reach', 'far-reach'].map(cat => {
          const count = analyses.filter(a => a.analysis_category === cat).length;
          return count > 0 ? (
            <button
              key={cat}
              className={`history-filter-btn ${filter === cat ? 'active' : ''}`}
              onClick={() => setFilter(cat)}
            >
              {getCategoryLabel(cat)} ({count})
            </button>
          ) : null;
        })}
      </div>

      {/* Analyses List */}
      <div className="history-list">
        {filteredAnalyses.map(analysis => (
          <div key={analysis.id} className="history-card">
            <div className="history-card-content">
              <div className="history-university">
                <h3 className="history-uni-name">{analysis.university_name}</h3>
                <p className="history-card-date">
                  {new Date(analysis.created_at).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </p>
              </div>

              <div className="history-stats">
                <div className="history-stat">
                  <div
                    className="history-percentage"
                    style={{ color: getChanceColor(analysis.admit_chance_percentage) }}
                  >
                    {analysis.admit_chance_percentage}%
                  </div>
                  <div className="history-stat-label">Admit Chance</div>
                </div>

                <div className="history-stat">
                  <div className="history-category-badge">
                    {getCategoryLabel(analysis.analysis_category)}
                  </div>
                </div>
              </div>

              {analysis.matching_factors && analysis.matching_factors.length > 0 && (
                <div className="history-preview">
                  <p className="history-preview-label">Key matches:</p>
                  <p className="history-preview-text">
                    {analysis.matching_factors.slice(0, 1).join(', ')}
                  </p>
                </div>
              )}
            </div>

            <div className="history-card-actions">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onViewAnalysis?.(analysis.id)}
                icon={<Eye size={16} />}
              >
                View
              </Button>
              <button
                className="history-delete-btn"
                onClick={() => handleDeleteClick(analysis.id, analysis.university_name)}
                title="Delete analysis"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredAnalyses.length === 0 && (
        <div className="history-empty-filtered">
          <p>No analyses in this category</p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmModal.isOpen && (
        <div className="delete-modal-overlay">
          <div className="delete-modal">
            <div className="delete-modal-content">
              <h3>Delete Analysis</h3>
              <p>
                Are you sure you want to delete the analysis for <strong>{deleteConfirmModal.universityName}</strong>?
              </p>
              <p className="delete-warning">This action cannot be undone.</p>
            </div>
            <div className="delete-modal-footer">
              <button 
                className="delete-modal-btn-cancel"
                onClick={handleCancelDelete}
              >
                Cancel
              </button>
              <button 
                className="delete-modal-btn-confirm"
                onClick={handleConfirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalysisHistory;
